import { useEffect, useState } from 'react'
import { useAuthenticationDataContext } from '../../../Helpers/AuthenticationDataProvider';

import useFetchWrapper, { HttpResponse } from '../../../Helpers/useFetchWrapper';

import { Article, HookProps, TabStatus } from './ArticlesTab.model';
import { FormPopupField } from '../../../components/Popup/FormPopup.model';
import useObjectHelper from '../../../Helpers/useObjectHelper';
import { useIntlWrapperContext } from '../../../Helpers/IntlWrapperProvider';
import { researchFields } from '../../../Helpers/StaticData';
import { useIntl } from 'react-intl';

const useArticlesTab = ({triggerArticlePopup}: HookProps) => {
	const [articles, setArticles] = useState<Article[]>([]);
	const [editArticleForm, setEditArticleForm] = useState<string | undefined>();
	const [showArticleForm, setShowArticleForm] = useState(false);
  const [ tabStatus, setTabStatus ] = useState<TabStatus>(TabStatus.Loading);
  const { userId } = useAuthenticationDataContext();

	const { get, post, put, remove } = useFetchWrapper();

	const { arrToOptionList, objToOptionList } = useObjectHelper();

	const { i18nCountries } = useIntlWrapperContext();

	const intl = useIntl();
	
	
	const articleFields: FormPopupField[] = [
		{
			id: 'authors',
			layout: 'w100',
			options: {
				optionList: arrToOptionList(['Anonymous']),
				type: 'codeBased',
			},
			rules: { required: true },
			type: 'tags',
		},
		{
			id: 'title',
			layout: 'w50',
			rules: { required: true },
			type: 'text',
		},
		{
			id: 'link',
			layout: 'w100',
			type: 'text',
		},
		{
			id: 'publishedIn',
			layout: 'w100',
			rules: { required: true },
			type: 'text',
		},
		{
			id: 'publishingLocation',
			layout: 'w50',
			options: {
				optionList: objToOptionList(i18nCountries),
				type: 'codeBased',
			},
			rules: { required: true },
			type: 'select',
		},
		{
			id: 'publishingYear',
			layout: 'w50',
			rules: { required: true },
			type: 'year',
		},
		{
			id: 'tags',
			layout: 'w100',
			options: {
				optionList: arrToOptionList(researchFields.map((rf) => intl.formatMessage({id: `researchAreas.${rf}`}))),
				type: 'codeBased',
			},
			rules: { required: true },
			type: 'tags',
		},
		{
			id: 'description',
			layout: 'w100',
			rules: { required: true },
			type: 'textArea',
		},
	];

  useEffect(() => {
    getArticles();
	}, [userId]);

	const getArticles = async () => {
		const response: HttpResponse<Article[]> = await get(`/user/profile/article/${userId}`);

		const articles: Article[] = response.parsedBody?.map((article: Article) => ({
			articleId: article.articleId,
			authors: article.authors,
			description: article.description,
			link: article.link,
			publishedIn: article.publishedIn,
			publishingLocation: article.publishingLocation,
			publishingYear: article.publishingYear,
			tags: article.tags,
			title: article.title
		})) || [];

		setArticles(articles);

		setTabStatus(TabStatus.Loaded);
	}

	const formatData = (formData: any) => {
		if (formData.startDate)
			formData.startDate = formData.startDate.format('YYYY-MM-DD');
		if (formData.endDate)
			formData.endDate = formData.endDate.format('YYYY-MM-DD');
		if (formData.publishingYear)
			formData.publishingYear = formData.publishingYear.format('YYYY');
		Object.keys(formData).forEach((key) => {
			if (formData[key] === '') formData[key] = undefined;
		});
		return formData;
	};
	
	const submitArticle = async (formData: Article) => {
		formData = formatData(formData);
		await post('/user/profile/article', formData);
		getArticles();
	};

	const editArticle = async (formData: Article, id: string | undefined) => {
		formData = formatData(formData);
		await put(`/user/profile/article/${id}`, formData);
		getArticles();
	};

	const deleteArticle = async (articleId: string | undefined) => {
		await remove(`/user/profile/article/${articleId}`);
		getArticles();
	};

	const toggle = (
		set: React.Dispatch<React.SetStateAction<boolean>>,
		value: boolean
	) => {
		set(!value);
	};

	return {
		articleFields,
		deleteArticle,
		editArticle,
		setEditArticleForm,
    state: {
			articles,
			editArticleForm,
			showArticleForm: triggerArticlePopup.value,
      tabStatus,
		},
		submitArticle,
		toggleArticleForm: ()=> {toggle(triggerArticlePopup.set, triggerArticlePopup.value)}
  }

}
export default useArticlesTab;